import React, { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIERS = {
  elite: {
    label: 'Elite',
    color: '#22c55e',
    bg: 'bg-green-50',
    border: 'border-green-400',
    meterWidth: '95%',
    meterColor: 'from-green-400 to-green-600',
    meterLabel: 'Excellent',
  },
  strong: {
    label: 'Strong',
    color: '#3b82f6',
    bg: 'bg-blue-50',
    border: 'border-blue-400',
    meterWidth: '75%',
    meterColor: 'from-blue-400 to-blue-600',
    meterLabel: 'Great',
  },
  flexible: {
    label: 'Flexible',
    color: '#f59e0b',
    bg: 'bg-amber-50',
    border: 'border-amber-400',
    meterWidth: '50%',
    meterColor: 'from-amber-400 to-amber-500',
    meterLabel: 'Good Options',
  },
  challenged: {
    label: 'Challenged',
    color: '#ef4444',
    bg: 'bg-orange-50',
    border: 'border-orange-400',
    meterWidth: '25%',
    meterColor: 'from-orange-400 to-red-500',
    meterLabel: 'Work in Progress',
  },
};

function getTier(creditScore) {
  switch (creditScore) {
    case 'excellent': return 'elite';
    case 'good': return 'strong';
    case 'fair': return 'flexible';
    case 'poor': return 'challenged';
    default: return 'flexible';
  }
}

function getTierMessage(tier, isHomeowner, isNewEquipment, yearsInBusiness) {
  let msg = '';
  let extra = '';

  switch (tier) {
    case 'elite':
      msg = "We've got great news! Based on your profile, you're looking at top-tier, bank-grade rates. Since you have excellent credit";
      msg += isHomeowner
        ? " (and being a homeowner adds even more weight), we can likely secure your equipment with little-to-no hassle."
        : ", we can likely secure your equipment with little-to-no hassle.";
      msg += " Let's get this across the finish line\u2014submit your details now for an official approval!";
      break;
    case 'strong':
      msg = "You're in a great spot! We've got several solid financing options for you. With a score in this range, we can usually lock in some competitive rates.";
      msg += " Depending on the age of that equipment, we might look at a standard down payment to get the best terms.";
      if (isHomeowner) msg += " Since you're a homeowner, we have even more leverage with our lenders.";
      msg += " Let's get your quote started!";
      break;
    case 'flexible':
      msg = "We can work with this! We specialize in finding paths forward when the big banks say no.";
      msg += " We have specialized lenders for this range. We'll likely need a bit more info on the equipment and your business history to get the best possible quote.";
      msg += " Head to the final step so we can shop this to the right partners for you!";
      break;
    case 'challenged':
      msg = "Let's be real\u2014this one might be a bit of a climb, but we aren't counting you out.";
      msg += " At this credit level, traditional deals are tough to pin down. To give us the best shot at an approval, do you have a co-signer with stronger credit?";
      msg += " If not, we'll still take a look to see if your equipment equity can bridge the gap. Fill out your info and let's see what we can do.";
      break;
    default:
      msg = "We'd love to look at your deal. Submit your info below and we'll get back to you!";
  }

  if (isNewEquipment && tier !== 'elite') {
    extra = "Great news\u2014newer equipment like yours helps secure lower rates with our lenders.";
  }

  // Business years messaging
  let bizExtra = '';
  if (yearsInBusiness === '3plus') {
    bizExtra = "Your established business track record is a strong asset with our lenders.";
  } else if (yearsInBusiness === '1to3') {
    bizExtra = "With a newer business, we may weigh more on personal credit\u2014but we have lenders who work with businesses at every stage.";
  } else if (yearsInBusiness === 'startup') {
    bizExtra = "As a startup, we'll lean more on personal credit and may explore collateral options. Don't worry\u2014we've helped many new businesses get funded.";
  }

  return { msg, extra, bizExtra };
}


const ConfidenceMeter = ({ tier }) => {
  const t = TIERS[tier];
  return (
    <div data-testid="confidence-meter" className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-[#333]">Your Confidence Level</span>
        <span className="text-sm font-bold" style={{ color: t.color }}>{t.meterLabel}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
        <div
          className={`h-4 rounded-full bg-gradient-to-r ${t.meterColor} transition-all duration-1000 ease-out`}
          style={{ width: t.meterWidth }}
        />
      </div>
      <div className="flex justify-between mt-1 text-[10px] text-gray-400 font-medium">
        <span>TOUGH</span>
        <span>GOOD</span>
        <span>GREAT</span>
        <span>EXCELLENT</span>
      </div>
    </div>
  );
};


const PreApprovalWidget = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showResults, setShowResults] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    equipmentYear: '',
    equipmentPrice: 50000,
    downPayment: '',
    yearsInBusiness: '',
    creditScore: '',
    homeOwnership: '',
  });
  const [contactData, setContactData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  const totalSteps = 6;
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 30 }, (_, i) => currentYear - i);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleContactChange = (field, value) => {
    setContactData(prev => ({ ...prev, [field]: value }));
  };

  const nextStep = () => { if (currentStep < totalSteps) setCurrentStep(currentStep + 1); };
  const prevStep = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return formData.equipmentYear !== '';
      case 2: return formData.equipmentPrice > 0;
      case 3: return formData.downPayment !== '';
      case 4: return formData.yearsInBusiness !== '';
      case 5: return formData.creditScore !== '';
      case 6: return formData.homeOwnership !== '';
      default: return false;
    }
  };

  const canSubmitContact = () => {
    return contactData.firstName.trim() && contactData.lastName.trim() && contactData.email.trim() && contactData.phone.trim();
  };

  const handleSeeOptions = () => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'pre_approval_see_options');
    }
    setShowResults(true);
  };

  const handlePersonalUseEscape = () => {
    const el = document.getElementById('ready-to-apply');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleReadyToApply = () => {
    const el = document.getElementById('ready-to-apply');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleContactSubmit = async () => {
    setIsSubmitting(true);
    setError('');

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        send_to: 'AW-11080823629/ihQUCJ6gr-MbEM3m36Mp',
        value: 400.0,
        currency: 'CAD',
      });
      window.gtag('event', 'pre_approval_submission');
    }

    try {
      await axios.post(`${API}/pre-approval`, {
        equipment_year: formData.equipmentYear,
        equipment_price: formData.equipmentPrice,
        down_payment: parseFloat(formData.downPayment) || 0,
        years_in_business: formData.yearsInBusiness,
        credit_score: formData.creditScore,
        home_ownership: formData.homeOwnership,
        first_name: contactData.firstName,
        last_name: contactData.lastName,
        email: contactData.email,
        phone: contactData.phone,
      });
      setIsSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again or call us at 1-844-250-EASE.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const tier = getTier(formData.creditScore);
  const tierInfo = TIERS[tier];
  const isHomeowner = formData.homeOwnership === 'yes';
  const equipAge = formData.equipmentYear ? currentYear - parseInt(formData.equipmentYear) : 99;
  const isNewEquipment = equipAge <= 5;
  const { msg: tierMessage, extra: machineBoost, bizExtra } = getTierMessage(tier, isHomeowner, isNewEquipment, formData.yearsInBusiness);

  // ---- Final Success State ----
  if (isSubmitted) {
    return (
      <div data-testid="preapproval-success" className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto border-t-4 border-[#FF6600]">
        <div className="text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-[#333] mb-4">You're All Set, {contactData.firstName}!</h3>
          <p className="text-[#4A4A4A] text-lg mb-6">
            A financing specialist will contact you within <span className="font-semibold text-[#FF6600]">2 business hours</span> with your estimated rates.
          </p>
          <div className="bg-gray-50 rounded-lg p-6 text-left mb-6">
            <h4 className="font-semibold text-[#333] mb-3">Your Submission Summary:</h4>
            <ul className="space-y-2 text-sm text-[#4A4A4A]">
              <li><span className="font-medium">Name:</span> {contactData.firstName} {contactData.lastName}</li>
              <li><span className="font-medium">Email:</span> {contactData.email}</li>
              <li><span className="font-medium">Phone:</span> {contactData.phone}</li>
              <li><span className="font-medium">Equipment:</span> {formData.equipmentYear} — {formatCurrency(formData.equipmentPrice)}</li>
              <li><span className="font-medium">Financing Amount:</span> {formatCurrency(formData.equipmentPrice - (parseFloat(formData.downPayment) || 0))}</li>
            </ul>
          </div>
          <button
            data-testid="ready-to-apply-btn"
            onClick={handleReadyToApply}
            className="inline-flex items-center bg-[#FF6600] hover:bg-[#FF9933] text-white px-8 py-3.5 rounded-xl font-bold text-base transition-all shadow-lg hover:shadow-xl"
          >
            Ready to Apply Now?
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
          </button>
          <p className="mt-4 text-sm text-[#666]">
            Questions? Call us at <a href="tel:1-844-250-3273" className="text-[#FF6600] font-semibold hover:underline">1-844-250-EASE</a>
          </p>
        </div>
      </div>
    );
  }

  // ---- Results + Contact Form State ----
  if (showResults) {
    return (
      <div data-testid="preapproval-results" className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-2xl mx-auto border-t-4 border-[#FF6600]">
        <div className="bg-gradient-to-r from-[#333] to-[#4A4A4A] px-8 py-5 text-white">
          <h3 className="text-xl font-bold">Your Pre-Approval Results</h3>
        </div>

        <div className="p-8">
          <ConfidenceMeter tier={tier} />

          <div data-testid="tier-message" className={`rounded-xl p-6 mb-2 border-l-4 ${tierInfo.bg} ${tierInfo.border}`}>
            <p className="text-[#333] leading-relaxed">{tierMessage}</p>
            {machineBoost && (
              <p className="mt-3 text-sm font-medium" style={{ color: tierInfo.color }}>{machineBoost}</p>
            )}
            {bizExtra && (
              <p className="mt-2 text-sm font-medium text-[#555]">{bizExtra}</p>
            )}
          </div>

          <div className="flex flex-wrap gap-3 text-xs text-[#666] mb-8 mt-4">
            <span className="bg-gray-100 px-3 py-1 rounded-full">Equipment: {formData.equipmentYear}</span>
            <span className="bg-gray-100 px-3 py-1 rounded-full">Price: {formatCurrency(formData.equipmentPrice)}</span>
            <span className="bg-gray-100 px-3 py-1 rounded-full">Down: {formatCurrency(parseFloat(formData.downPayment) || 0)}</span>
            <span className="bg-gray-100 px-3 py-1 rounded-full">Business: {formData.yearsInBusiness === '3plus' ? '3+ yrs' : formData.yearsInBusiness === '1to3' ? '1-3 yrs' : 'Startup'}</span>
            <span className="bg-gray-100 px-3 py-1 rounded-full">Homeowner: {isHomeowner ? 'Yes' : 'No'}</span>
          </div>

          <div className="border-t border-gray-100 pt-6">
            <h4 className="text-lg font-bold text-[#333] mb-1">Almost there — let's lock this in</h4>
            <p className="text-sm text-[#666] mb-5">Enter your contact info and a specialist will reach out with your official quote.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-[#4A4A4A] mb-1">First Name</label>
                <input
                  data-testid="contact-first-name"
                  type="text"
                  value={contactData.firstName}
                  onChange={(e) => handleContactChange('firstName', e.target.value)}
                  placeholder="John"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FF6600] focus:border-[#FF6600] transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#4A4A4A] mb-1">Last Name</label>
                <input
                  data-testid="contact-last-name"
                  type="text"
                  value={contactData.lastName}
                  onChange={(e) => handleContactChange('lastName', e.target.value)}
                  placeholder="Smith"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FF6600] focus:border-[#FF6600] transition-all"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-[#4A4A4A] mb-1">Email</label>
                <input
                  data-testid="contact-email"
                  type="email"
                  value={contactData.email}
                  onChange={(e) => handleContactChange('email', e.target.value)}
                  placeholder="john@company.ca"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FF6600] focus:border-[#FF6600] transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#4A4A4A] mb-1">Phone</label>
                <input
                  data-testid="contact-phone"
                  type="tel"
                  value={contactData.phone}
                  onChange={(e) => handleContactChange('phone', e.target.value)}
                  placeholder="(416) 555-1234"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FF6600] focus:border-[#FF6600] transition-all"
                />
              </div>
            </div>

            {error && (
              <div data-testid="preapproval-error" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm text-center">
                {error}
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-center gap-3">
              <button
                data-testid="preapproval-back-to-form"
                onClick={() => setShowResults(false)}
                className="text-[#666] hover:text-[#333] font-semibold text-sm transition-colors order-2 sm:order-1"
              >
                &larr; Edit My Answers
              </button>
              <button
                data-testid="preapproval-submit-btn"
                onClick={handleContactSubmit}
                disabled={!canSubmitContact() || isSubmitting}
                className={`flex-1 w-full sm:w-auto flex items-center justify-center px-8 py-3.5 rounded-xl font-semibold text-base transition-all order-1 sm:order-2 ${
                  canSubmitContact() && !isSubmitting
                    ? 'bg-[#FF6600] hover:bg-[#FF9933] text-white shadow-lg hover:shadow-xl'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Get My Official Quote'
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 px-8 py-4 border-t border-gray-100">
          <p className="text-[10px] text-[#999] leading-relaxed text-center">
            All pre-approvals are estimates only and are subject to actual fact-gathering, credit verification, and lender approval to produce an accurate quote.
            Rates and terms may vary based on final documentation. EquipEASE Lease Co. does not guarantee approval or specific terms until a formal application is reviewed.
          </p>
        </div>
      </div>
    );
  }

  // ---- Multi-Step Form ----
  return (
    <div data-testid="preapproval-widget" className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-2xl mx-auto border-t-4 border-[#FF6600]">
      <div className="bg-gradient-to-r from-[#333] to-[#4A4A4A] px-8 py-6 text-white">
        <h3 className="text-2xl font-bold mb-2">Financing Pre-Approval</h3>
        <p className="text-gray-300 text-sm">Get your estimated rates in minutes</p>
      </div>

      <div className="px-8 pt-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-[#4A4A4A]">Step {currentStep} of {totalSteps}</span>
          <span className="text-sm text-[#666]">{Math.round((currentStep / totalSteps) * 100)}% Complete</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className="bg-[#FF6600] h-2.5 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          />
        </div>
        <div className="flex justify-between mt-3">
          {[1, 2, 3, 4, 5, 6].map((step) => (
            <div
              key={step}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 ${
                step < currentStep
                  ? 'bg-[#FF6600] text-white'
                  : step === currentStep
                    ? 'bg-[#FF6600] text-white ring-4 ring-[#FF6600]/30'
                    : 'bg-gray-200 text-gray-500'
              }`}
            >
              {step < currentStep ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : step}
            </div>
          ))}
        </div>
      </div>

      <div className="p-8">
        {/* Step 1: Equipment Year */}
        {currentStep === 1 && (
          <div className="space-y-4">
            <label className="block">
              <span className="text-lg font-semibold text-[#333] mb-3 block">What year is the equipment?</span>
              <p className="text-sm text-[#666] mb-4">Select the model year of the equipment you're looking to finance.</p>
              <select
                data-testid="input-equipment-year"
                value={formData.equipmentYear}
                onChange={(e) => handleInputChange('equipmentYear', e.target.value)}
                className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FF6600] focus:border-[#FF6600] transition-all text-lg"
              >
                <option value="">Select Year</option>
                {years.map(year => <option key={year} value={year}>{year}</option>)}
              </select>
            </label>
          </div>
        )}

        {/* Step 2: Equipment Price */}
        {currentStep === 2 && (
          <div className="space-y-4">
            <label className="block">
              <span className="text-lg font-semibold text-[#333] mb-3 block">Estimated Equipment Price</span>
              <p className="text-sm text-[#666] mb-4">Drag the slider or enter the approximate purchase price.</p>
              <div className="text-center mb-6">
                <span className="text-4xl font-bold text-[#FF6600]">{formatCurrency(formData.equipmentPrice)}</span>
              </div>
              <input
                data-testid="input-equipment-price"
                type="range"
                min="5000"
                max="500000"
                step="5000"
                value={formData.equipmentPrice}
                onChange={(e) => handleInputChange('equipmentPrice', parseInt(e.target.value))}
                className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider-orange"
              />
              <div className="flex justify-between text-sm text-[#666] mt-2">
                <span>$5,000</span>
                <span>$500,000</span>
              </div>
            </label>
          </div>
        )}

        {/* Step 3: Down Payment */}
        {currentStep === 3 && (
          <div className="space-y-4">
            <label className="block">
              <span className="text-lg font-semibold text-[#333] mb-3 block">How much can you put down?</span>
              <p className="text-sm text-[#666] mb-4">Enter your estimated down payment amount.</p>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#666] text-lg">$</span>
                <input
                  data-testid="input-down-payment"
                  type="number"
                  value={formData.downPayment}
                  onChange={(e) => handleInputChange('downPayment', e.target.value)}
                  placeholder="10,000"
                  className="w-full pl-10 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FF6600] focus:border-[#FF6600] transition-all text-lg"
                />
              </div>
              {formData.equipmentPrice > 0 && formData.downPayment > 0 && (
                <div className="mt-4 p-4 bg-[#FF6600]/10 rounded-lg">
                  <p className="text-sm text-[#4A4A4A]">
                    <span className="font-semibold">Financing Amount:</span>{' '}
                    <span className="text-[#FF6600] font-bold">{formatCurrency(formData.equipmentPrice - formData.downPayment)}</span>
                  </p>
                </div>
              )}
            </label>
          </div>
        )}

        {/* Step 4: Years in Business */}
        {currentStep === 4 && (
          <div className="space-y-4">
            <span className="text-lg font-semibold text-[#333] mb-3 block">How many years in business?</span>
            <p className="text-sm text-[#666] mb-4">Your business history helps us find the best lender match.</p>
            <div className="grid gap-3">
              {[
                { value: 'startup', label: '0 / Startup', desc: 'Just getting started', icon: 'M12 4v16m8-8H4' },
                { value: '1to3', label: '1 \u2013 3 Years', desc: 'Building momentum', icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' },
                { value: '3plus', label: '3+ Years', desc: 'Established business', icon: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z' },
              ].map((option) => (
                <label
                  key={option.value}
                  data-testid={`years-in-business-${option.value}`}
                  className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    formData.yearsInBusiness === option.value
                      ? 'border-[#FF6600] bg-[#FF6600]/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="yearsInBusiness"
                    value={option.value}
                    checked={formData.yearsInBusiness === option.value}
                    onChange={(e) => handleInputChange('yearsInBusiness', e.target.value)}
                    className="sr-only"
                  />
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                    <svg className="w-5 h-5 text-[#4A4A4A]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d={option.icon} />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <span className="font-semibold text-[#333]">{option.label}</span>
                    <span className="text-[#999] ml-2 text-sm">{option.desc}</span>
                  </div>
                  {formData.yearsInBusiness === option.value && (
                    <svg className="w-6 h-6 text-[#FF6600]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                </label>
              ))}
            </div>

            {/* Personal Use Escape */}
            <div className="mt-5 pt-5 border-t border-gray-100 text-center">
              <button
                data-testid="personal-use-escape-btn"
                onClick={handlePersonalUseEscape}
                className="inline-flex items-center text-[#666] hover:text-[#FF6600] font-semibold text-sm transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                </svg>
                Not in Business? Apply for Personal Use Instead &rarr;
              </button>
            </div>
          </div>
        )}

        {/* Step 5: Credit Score */}
        {currentStep === 5 && (
          <div className="space-y-4">
            <span className="text-lg font-semibold text-[#333] mb-3 block">Estimated Credit Score</span>
            <p className="text-sm text-[#666] mb-4">Select your approximate credit score range.</p>
            <div className="grid gap-3">
              {[
                { value: 'excellent', label: 'Excellent', range: '720+', color: 'bg-green-500' },
                { value: 'good', label: 'Good', range: '620 \u2013 719', color: 'bg-blue-500' },
                { value: 'fair', label: 'Fair', range: '520 \u2013 619', color: 'bg-amber-500' },
                { value: 'poor', label: 'Needs Work', range: 'Below 520', color: 'bg-red-500' },
              ].map((option) => (
                <label
                  key={option.value}
                  data-testid={`credit-score-${option.value}`}
                  className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    formData.creditScore === option.value
                      ? 'border-[#FF6600] bg-[#FF6600]/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="creditScore"
                    value={option.value}
                    checked={formData.creditScore === option.value}
                    onChange={(e) => handleInputChange('creditScore', e.target.value)}
                    className="sr-only"
                  />
                  <div className={`w-3 h-3 rounded-full ${option.color} mr-4`} />
                  <div className="flex-1">
                    <span className="font-semibold text-[#333]">{option.label}</span>
                    <span className="text-[#666] ml-2">({option.range})</span>
                  </div>
                  {formData.creditScore === option.value && (
                    <svg className="w-6 h-6 text-[#FF6600]" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Step 6: Home Ownership */}
        {currentStep === 6 && (
          <div className="space-y-4">
            <span className="text-lg font-semibold text-[#333] mb-3 block">Do you own a home?</span>
            <p className="text-sm text-[#666] mb-4">Home ownership can help strengthen your application.</p>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: 'yes', label: 'Yes', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
                { value: 'no', label: 'No', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
              ].map((option) => (
                <label
                  key={option.value}
                  data-testid={`home-ownership-${option.value}`}
                  className={`flex flex-col items-center p-6 border-2 rounded-xl cursor-pointer transition-all ${
                    formData.homeOwnership === option.value
                      ? 'border-[#FF6600] bg-[#FF6600]/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="homeOwnership"
                    value={option.value}
                    checked={formData.homeOwnership === option.value}
                    onChange={(e) => handleInputChange('homeOwnership', e.target.value)}
                    className="sr-only"
                  />
                  <svg className="w-10 h-10 mb-2 text-[#4A4A4A]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d={option.icon} />
                  </svg>
                  <span className="font-semibold text-[#333] text-lg">{option.label}</span>
                  {formData.homeOwnership === option.value && (
                    <svg className="w-6 h-6 text-[#FF6600] mt-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex flex-col mt-8 pt-6 border-t border-gray-100">
          <div className="flex justify-between">
            {currentStep > 1 ? (
              <button
                data-testid="preapproval-back-btn"
                onClick={prevStep}
                className="flex items-center px-6 py-3 text-[#666] hover:text-[#333] font-semibold transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
            ) : <div />}

            {currentStep < totalSteps ? (
              <button
                data-testid="preapproval-continue-btn"
                onClick={nextStep}
                disabled={!canProceed()}
                className={`flex items-center px-8 py-3 rounded-xl font-semibold transition-all ${
                  canProceed()
                    ? 'bg-[#FF6600] hover:bg-[#FF9933] text-white shadow-lg hover:shadow-xl'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Continue
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <button
                data-testid="preapproval-see-options-btn"
                onClick={handleSeeOptions}
                disabled={!canProceed()}
                className={`flex items-center px-8 py-3 rounded-xl font-semibold transition-all ${
                  canProceed()
                    ? 'bg-[#FF6600] hover:bg-[#FF9933] text-white shadow-lg hover:shadow-xl'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                See My Options
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-gray-50 px-8 py-4 border-t border-gray-100">
        <div className="flex items-center justify-center gap-6 text-sm text-[#666]">
          <div className="flex items-center">
            <svg className="w-4 h-4 mr-1 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            Secure & Private
          </div>
          <div className="flex items-center">
            <svg className="w-4 h-4 mr-1 text-[#FF6600]" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
            2-Hour Response
          </div>
          <div className="flex items-center">
            <svg className="w-4 h-4 mr-1 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
              <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
            </svg>
            No Credit Check
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreApprovalWidget;
